import React, { useState, useEffect, useRef } from 'react';
import { Link, Redirect } from 'react-router-dom';
import ApprovalStatus from '../Modals/ApprovalStatus';
import { Modal } from '@mui/material';
import CasesAPI from 'api/CasesAPI';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { handleUpdateTicketNotification } from 'utils/NotificationsFuncs';
import StreamDisplay from 'components/TradeHelper/StreamDisplay';
import { classificationTicketReportPDF } from 'components/Common/EntityTicketPdf';
import EmailAPI from 'api/EmailAPI';
import 'resources/styles/chatkit-classify.css';

type CrossRulingLink = {
  title: string;
  url: string;
  relevance_percent: number | null;
  relevance_note: string | null;
};

const normalizeCrossRuling = (item: any): CrossRulingLink | null => {
  if (typeof item === 'string') {
    return {
      title: item,
      url: item,
      relevance_percent: null,
      relevance_note: null,
    };
  }

  if (!item || typeof item !== 'object') {
    return null;
  }

  const url = item?.url ?? item?.href ?? '';
  const relevancePercent =
    typeof item?.relevance_percent === 'number' ? item.relevance_percent : null;

  if (!url) {
    return null;
  }

  return {
    title: item?.title ?? url ?? 'Untitled ruling',
    url: url,
    relevance_percent: relevancePercent,
    relevance_note: item?.relevance_note ?? null,
  };
};

const isUrlOnlyCrossRuling = (item: CrossRulingLink) =>
  (!item.title || item.title === item.url) &&
  item.relevance_percent === null &&
  !item.relevance_note;

const getChatMessageText = (message: any) => {
  if (typeof message?.text === 'string') {
    return message.text;
  }

  if (typeof message?.content === 'string') {
    return message.content;
  }

  if (Array.isArray(message?.content)) {
    return message.content
      .map((part: any) => {
        if (typeof part === 'string') return part;
        if (typeof part?.text === 'string') return part.text;
        if (typeof part?.text?.value === 'string') return part.text.value;
        if (typeof part?.content === 'string') return part.content;
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  return '';
};

const formatChatRole = (role: string) => {
  if (role === 'user') return 'User';
  if (role === 'assistant') return 'BITE Assistant';
  if (role === 'tool') return 'Tool Output';
  return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Message';
};

const formatDisplayValue = (value: any) => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value === null || value === undefined) return '';
  return JSON.stringify(value, null, 2);
};

interface IClassResultsProps {
  cur_user: any;
  cur_case: any;
  trigger: boolean;
  setTrigger: any;
  triggerCSV: boolean;
  setTriggerCSV: any;
  triggerSendPdf?: boolean;
  setTriggerSendPdf?: any;
  sendPdfFileName?: string;
  sendPdfEmailRecipients?: string[];
  onTariffPdfSendComplete?: (success: boolean) => void;
}

const ClassResultsComponent: React.FC<IClassResultsProps> = ({
  cur_user,
  cur_case,
  trigger,
  setTrigger,
  triggerCSV,
  setTriggerCSV,
  triggerSendPdf = false,
  setTriggerSendPdf,
  sendPdfFileName,
  sendPdfEmailRecipients = [],
  onTariffPdfSendComplete,
}) => {
  const history = useHistory();

  const componentRef = useRef<HTMLDivElement>(null);
  const htsFinalReportRef = useRef<HTMLDivElement>(null);

  if (!cur_case) {
    history.push('/bite-monitor');
    return <></>;
  }
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);
  const [chatHistoryMaxHeight, setChatHistoryMaxHeight] = useState<
    number | null
  >(null);

  const { enqueueSnackbar } = useSnackbar();

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleApprovalCloseModal = () => {
    setIsApprovalModalOpen(false);
  };

  const changeStatus = async (newStatus: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the status of the ticket from ${cur_case.data.status} to ${newStatus}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      status: newStatus,
      activity: [...cur_case.data.activity, actObj],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id === cur_case.data.createdBy
      ) {
        cur_case.data.sharedWith.forEach(async (user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      } else if (
        cur_case.data.sharedWith.length > 0 &&
        cur_user.basics.Id !== cur_case.data.createdBy
      ) {
        const shareArr = cur_case.data.sharedWith.filter((user: string) => {
          return user !== cur_user.basics.id && user !== cur_user.basics.email;
        });
        shareArr.push(cur_case.data.createdBy);
        shareArr.forEach(async (user: string) => {
          handleUpdateTicketNotification(
            cur_case.data.ticketName,
            cur_user,
            user,
            cur_case.data._id,
          );
        });
      }
    } else {
      enqueueSnackbar('There was an error updating the status', {
        variant: 'error',
      });
    }
    setTrigger(!trigger);
  };

  const handleCSV = async () => {
    await classificationTicketReportPDF(
      buildClassificationReportData(),
      `${cur_case.data.ticketName}.pdf`,
    );
    setTriggerCSV(false);
  };

  const blobToBase64 = async (blob: Blob): Promise<string> => {
    const buffer = await blob.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;

    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }

    return btoa(binary);
  };

  const handleSendPdf = async () => {
    let sendSucceeded = false;

    try {
      const resolvedFileName = `${
        sendPdfFileName || cur_case.data.ticketName || 'result_ticket'
      }.pdf`;
      const pdfBlob = await classificationTicketReportPDF(
        buildClassificationReportData(),
        resolvedFileName,
        {
          shouldDownload: false,
        },
      );

      if (!pdfBlob || sendPdfEmailRecipients.length === 0) {
        throw new Error('Please add at least one recipient email');
      }

      const pdfBase64 = await blobToBase64(pdfBlob);
      const senderName = `${cur_user?.basics?.firstName || ''} ${
        cur_user?.basics?.lastName || ''
      }`.trim();
      const emailRes = await EmailAPI.sendTariffResultPdf({
        recipients: sendPdfEmailRecipients,
        fileName: resolvedFileName,
        pdfBase64: pdfBase64,
        docType: cur_case?.data?.docType,
        ticketId: cur_case?.data?._id,
        ticketName: cur_case?.data?.ticketName,
        senderName: senderName,
        senderEmail: cur_user?.basics?.email,
      });

      if (emailRes?.success) {
        sendSucceeded = true;
        enqueueSnackbar('PDF emailed successfully', { variant: 'success' });
      } else {
        throw new Error(emailRes?.result || 'Failed to send PDF email');
      }
    } catch (error: any) {
      enqueueSnackbar(
        `Failed to send result PDF: ${error?.message || 'Unknown error'}`,
        { variant: 'error' },
      );
    } finally {
      setTriggerSendPdf?.(false);
      onTariffPdfSendComplete?.(sendSucceeded);
    }
  };

  useEffect(() => {
    const initialResponses = (
      Array.isArray(cur_case.data.followUps) ? cur_case.data.followUps : []
    ).reduce((acc: any, item: any) => {
      acc[item.question] = item.answer || '';
      return acc;
    }, {} as Record<string, string>);
    setResponses(initialResponses);
  }, []);

  useEffect(() => {
    if (triggerCSV) {
      handleCSV();
    }
  }, [triggerCSV]);

  useEffect(() => {
    if (triggerSendPdf) {
      handleSendPdf();
    }
  }, [triggerSendPdf]);

  const crossRulingLinks: CrossRulingLink[] = Array.isArray(
    cur_case.data?.crossRulings,
  )
    ? cur_case.data.crossRulings
        .map(normalizeCrossRuling)
        .filter((item: any): item is CrossRulingLink => Boolean(item))
    : [];
  const visibleCrossRulingLinks = isExpandedCross
    ? crossRulingLinks
    : crossRulingLinks.slice(0, 5);
  const hasOnlyUrlCrossRulings =
    crossRulingLinks.length > 0 && crossRulingLinks.every(isUrlOnlyCrossRuling);
  const htsChatHistory = cur_case.data?.htsChatHistory;
  const exactChatMessages =
    htsChatHistory?.chatkitThread?.available &&
    Array.isArray(htsChatHistory?.chatkitThread?.messages)
      ? htsChatHistory.chatkitThread.messages
      : [];
  const observedChatMessages = Array.isArray(htsChatHistory?.observed?.messages)
    ? htsChatHistory.observed.messages
    : [];
  const chatHistoryMessages =
    exactChatMessages.length > 0 ? exactChatMessages : observedChatMessages;
  const htsFinalReport = cur_case.data?.htsFinalReport;

  useEffect(() => {
    const finalReportElement = htsFinalReportRef.current;

    if (!finalReportElement || !chatHistoryMessages.length || !htsFinalReport) {
      setChatHistoryMaxHeight(null);
      return undefined;
    }

    const updateChatHistoryMaxHeight = () => {
      const height = Math.ceil(
        finalReportElement.getBoundingClientRect().height,
      );
      setChatHistoryMaxHeight(height > 0 ? height : null);
    };

    updateChatHistoryMaxHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateChatHistoryMaxHeight);

      return () => {
        window.removeEventListener('resize', updateChatHistoryMaxHeight);
      };
    }

    const resizeObserver = new ResizeObserver(updateChatHistoryMaxHeight);
    resizeObserver.observe(finalReportElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [chatHistoryMessages.length, htsFinalReport]);

  const isLegacyClassificationTicket =
    !htsFinalReport &&
    !htsChatHistory?.threadId &&
    !htsChatHistory?.source &&
    exactChatMessages.length === 0 &&
    observedChatMessages.length === 0;

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]"]/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: React.ReactElement[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  const renderTextBlock = (label: string, value: any) => {
    // Only skip genuinely-empty values. formatDisplayValue renders numbers and
    // booleans, so a `!value` guard would wrongly drop 0 / false.
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return (
      <div className='bite-ticket-report-field'>
        <div className='bite-ticket-report-label'>{label}</div>
        <div className='bite-ticket-report-value'>
          {formatDisplayValue(value)}
        </div>
      </div>
    );
  };

  const renderListBlock = (label: string, values: any[]) => {
    if (!Array.isArray(values) || values.length === 0) {
      return null;
    }

    return (
      <div className='bite-ticket-report-field'>
        <div className='bite-ticket-report-label'>{label}</div>
        <ul className='bite-ticket-report-list'>
          {values.map((value, index) => (
            <li key={`${label}-${index}`}>{formatDisplayValue(value)}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderChatHistory = () => {
    if (!chatHistoryMessages.length) {
      return null;
    }

    return (
      <div
        className='bite-ticket-section bite-ticket-chat-section'
        style={
          chatHistoryMaxHeight ? { maxHeight: chatHistoryMaxHeight } : undefined
        }
      >
        <div className='bite-ticket-section-header'>
          <div>
            <h2>Chat History</h2>
            <p>
              {exactChatMessages.length > 0
                ? 'Exact ChatKit thread transcript'
                : 'App-observed transcript'}
            </p>
          </div>
        </div>
        <div className='bite-ticket-chat-list'>
          {chatHistoryMessages.map((message: any, index: number) => {
            const text = getChatMessageText(message);
            if (!text) {
              return null;
            }

            return (
              <div
                key={message?.id || `chat-history-${index}`}
                className={`bite-ticket-chat-message ${
                  message?.role === 'user' ? 'user' : 'assistant'
                }`}
              >
                <div className='bite-ticket-chat-role'>
                  {formatChatRole(message?.role)}
                </div>
                <div className='bite-ticket-chat-text'>{text}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFinalReport = () => {
    if (!htsFinalReport) {
      return null;
    }

    const payload = htsFinalReport.payload || {};
    const generated = htsFinalReport.generated || {};

    return (
      <div
        className='bite-ticket-section bite-ticket-report-section'
        ref={htsFinalReportRef}
      >
        <div className='bite-ticket-section-header'>
          <div>
            <h2>Final Report</h2>
            <p>
              {payload.product_desc ||
                cur_case.data.classificationValue ||
                'Saved HTS classification report'}
            </p>
          </div>
        </div>
        <div className='bite-ticket-report-grid'>
          {renderTextBlock('HTS Code', payload.hts_code || generated.hts_code)}
          {renderTextBlock(
            'Generated Commodity Description',
            generated.cmdt_desc || cur_case.data?.commodity?.commodityName,
          )}
          {renderTextBlock(
            'Summary',
            payload.summary_text || generated.summary_text,
          )}
          {renderTextBlock(
            'Legal Basis',
            payload.legal_basis_summary || generated.legal_basis_summary,
          )}
          {renderTextBlock(
            'Confidence Note',
            payload.confidence_note || generated.confidence_note,
          )}
          {renderListBlock('Reasoning Trace', payload.reasoning_trace)}
          {renderListBlock('Facts Relied On', payload.facts_relied_on)}
        </div>
      </div>
    );
  };

  const classificationCodeLabel =
    cur_case.data.docType === 'ECCN' ? 'ECCN Code' : 'HTS Code';
  const classificationCode =
    cur_case.data.docType === 'ECCN'
      ? cur_case.data.commodity.eccn
      : cur_case.data.commodity.harmonizedScheduleCode;
  const commodityDescription =
    cur_case.data.commodity.commodityName || cur_case.data.classificationValue;
  const followUps = Array.isArray(cur_case.data.followUps)
    ? cur_case.data.followUps
    : [];
  const preparedBy =
    [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    cur_user?.basics?.email ||
    'BITE Classification';
  function buildClassificationReportData() {
    return {
      ticketName: cur_case.data.ticketName,
      docType: cur_case.data.docType,
      isLegacyTicket: isLegacyClassificationTicket,
      classificationValue: cur_case.data.classificationValue,
      commodity: {
        name: commodityDescription,
        codeLabel: classificationCodeLabel,
        code: classificationCode,
        material: cur_case.data.commodity?.material,
        intendedUse: cur_case.data.commodity?.intendedUse,
        physicalState: cur_case.data.commodity?.physicalState,
      },
      followUps: followUps.map((item: any) => ({
        question: item.question,
        answer: item.answer || responses[item.question] || '',
      })),
      chatHistory: chatHistoryMessages
        .map((message: any) => ({
          role: formatChatRole(message?.role),
          text: getChatMessageText(message),
        }))
        .filter((message: any) => message.text),
      crossRulings: crossRulingLinks,
      chapterNotes: cur_case.data.chapterNotes,
      finalReport: htsFinalReport,
      aiStream: cur_case.data.aiStream,
      preparedBy: preparedBy,
    };
  }

  return (
    <>
      {cur_case.data.docType === 'Classification' ? (
        <div
          className='results-card-frame'
          style={{ paddingBottom: '0px', gap: '12px' }}
        >
          <Link
            to={{
              pathname: '/tariff-checker',
              state: {
                primary: cur_case.data.commodity.harmonizedScheduleCode,
              },
            }}
          >
            <button className='btn-new-automate'>
              <div>Check Tariff Rate</div>
            </button>
          </Link>
          <Link
            to={{
              pathname: '/import-screening',
              state: {
                primary: cur_case.data.commodity.harmonizedScheduleCode,
              },
            }}
          >
            <button className='btn-new-automate'>
              <div>Check For Import Flags</div>
            </button>
          </Link>
        </div>
      ) : (
        <div
          className='results-card-frame'
          style={{ paddingBottom: '0px', gap: '12px' }}
        >
          <Link
            to={{
              pathname: '/bite-eccn',
              state: {
                primary: cur_case.data.commodity.eccn,
              },
            }}
          >
            <button className='btn-new-automate'>
              <div>ECCN Check</div>
            </button>
          </Link>
        </div>
      )}

      <div className='results-card-frame'>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Last Screen</div>
          <div className='results-card-frame-body'>N/A</div>
          <div className='results-card-frame-footer'></div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Screening Frequency</div>
          <div className='results-card-frame-body'>N/A</div>
          <div className='results-card-frame-footer'></div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Status</div>
          <div className='results-card-frame-body'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='9'
              height='8'
              viewBox='0 0 9 8'
              fill='none'
              style={{ marginRight: '5px' }}
            >
              <circle
                cx='4.5'
                cy='4'
                r='3'
                fill={
                  cur_case.data.status === 'Complete'
                    ? 'var(--card-text)'
                    : cur_case.data.status === 'Pending'
                    ? '#FDB022'
                    : cur_case.data.status === 'New Results'
                    ? '#4895EF'
                    : cur_case.data.status === 'Approved'
                    ? '#47CD89'
                    : cur_case.data.status === 'Rejected'
                    ? '#F04438'
                    : '#B67DFF'
                }
              />
            </svg>
            {cur_case.data.status}
          </div>
          <div
            className='results-card-frame-footer'
            onClick={() => setIsApprovalModalOpen(true)}
          >
            Change
          </div>
        </div>
        <div className='results-card-frame-comp'>
          <div className='results-card-frame-head'>Type</div>
          <div className='results-card-frame-body-doc'>
            <div className='results-card-frame-body-texts'>
              {cur_case.data.docType}
            </div>
          </div>
          <div className='results-card-frame-footer' style={{ height: '18px' }}>
            {' '}
          </div>
        </div>
      </div>
      <div className='results-frame-col' ref={componentRef}>
        {isLegacyClassificationTicket ? (
          <div
            className='results-frame-classify'
            style={{ marginBottom: '0px' }}
          >
            <div className='class-ticket-container'>
              <div className='class-ticket-left-div'>
                <div className='class-ticket-searched-val'>
                  <h1>Classification Description:</h1>
                  <h2>{cur_case.data.classificationValue}</h2>
                </div>
                <div className='class-ticket-searched-val'>
                  <h1>Optional Details:</h1>
                  <h2>Material: {cur_case.data.commodity.material || 'N/A'}</h2>
                  <h2>
                    Intended Use: {cur_case.data.commodity.intendedUse || 'N/A'}
                  </h2>
                  <h2>
                    Physical State:{' '}
                    {cur_case.data.commodity.physicalState || 'N/A'}
                  </h2>
                </div>
                <div className='class-ticket-results'>
                  <h1>Results:</h1>
                  <h2>
                    Product Description: {cur_case.data.commodity.commodityName}
                  </h2>
                  {cur_case.data.docType === 'ECCN' ? (
                    <h2>ECCN Code: {cur_case.data.commodity.eccn}</h2>
                  ) : (
                    <h2>
                      HTS Code: {cur_case.data.commodity.harmonizedScheduleCode}
                    </h2>
                  )}
                </div>
                <h1>Clarifying Questions:</h1>
                <div className='classify-body-ticket'>
                  {followUps.length > 0 ? (
                    followUps.map(({ question }: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          paddingTop: '15px',
                          width: '95%',
                        }}
                      >
                        <div className='classify-header-ticket'>{question}</div>
                        <div
                          className='classify-cont'
                          style={{ padding: '0px' }}
                        >
                          <div className='classify-cont-sec'>
                            <div className='classify-cont-sec-div'>
                              <input
                                placeholder='Enter details'
                                className='classify-cont-sec-input'
                                type='text'
                                disabled
                                value={responses[question] || ''}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <h3>None</h3>
                  )}
                </div>
              </div>
              <div style={{ width: '70%' }}>
                <div className='classify-results-card-list'>
                  <StreamDisplay
                    streamData={cur_case.data.aiStream}
                    ticket={true}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='bite-class-ticket-summary'>
              <div className='bite-class-ticket-summary-head'>
                <div>
                  <div className='bite-class-ticket-eyebrow'>
                    Saved Classification
                  </div>
                  <h2>{commodityDescription || 'Classification result'}</h2>
                </div>
                {classificationCode && (
                  <div className='bite-class-ticket-code'>
                    <span>{classificationCodeLabel}</span>
                    <strong>{classificationCode}</strong>
                  </div>
                )}
              </div>

              {followUps.length > 0 && (
                <div className='bite-class-ticket-followups'>
                  <div className='bite-class-ticket-followups-title'>
                    Clarifying Questions
                  </div>
                  <div className='bite-class-ticket-followups-list'>
                    {followUps.map(({ question }: any, index: number) => (
                      <div
                        className='bite-class-ticket-followup'
                        key={`${question}-${index}`}
                      >
                        <span>{question}</span>
                        <strong>
                          {responses[question] || 'No answer saved'}
                        </strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!htsFinalReport && (
                <div className='bite-class-ticket-stream'>
                  <StreamDisplay
                    streamData={cur_case.data.aiStream}
                    ticket={true}
                  />
                </div>
              )}
            </div>

            <div className='bite-class-ticket-main-row'>
              {renderChatHistory()}
              {renderFinalReport()}
            </div>
          </>
        )}

        <div
          className={
            isLegacyClassificationTicket
              ? 'api-container'
              : 'bite-class-ticket-reference'
          }
          style={
            isLegacyClassificationTicket
              ? { gap: '24px', padding: '0px 20px 15px 25px' }
              : undefined
          }
        >
          <div
            className='api-container-left'
            style={isLegacyClassificationTicket ? { flex: '1' } : undefined}
          >
            <h1 style={{ fontSize: '14px' }}>CROSS Rulings</h1>
            {hasOnlyUrlCrossRulings ? (
              <p
                className={
                  !isExpandedCross ? 'class-ticket-cross-expandeds' : ''
                }
              >
                {crossRulingLinks.map((item, index) => (
                  <React.Fragment key={`${item.url}-${index}`}>
                    <a
                      href={item.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='class-ticket-cross-rules'
                    >
                      {item.url}
                    </a>
                    <br />
                  </React.Fragment>
                ))}
              </p>
            ) : crossRulingLinks.length > 0 ? (
              <div
                className='bite-classify-cross-links'
                style={{ maxHeight: '220px' }}
              >
                {visibleCrossRulingLinks.map((item, index) => (
                  <div
                    key={`${item.url}-${index}`}
                    className='bite-classify-cross-link-card'
                  >
                    <a
                      href={item.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='bite-classify-cross-link-title'
                    >
                      {item.title || item.url}
                    </a>

                    {typeof item.relevance_percent === 'number' && (
                      <div className='bite-classify-cross-link-relevance'>
                        Relevance: {item.relevance_percent}%
                      </div>
                    )}

                    {item.relevance_note && (
                      <div className='bite-classify-cross-link-note'>
                        {item.relevance_note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className='bite-classify-path-awaiting'>
                Awaiting information...
              </p>
            )}
            {crossRulingLinks.length > 5 && (
              <div
                className='expl-notes-btn'
                onClick={() => setIsExpandedCross(!isExpandedCross)}
              >
                {!isExpandedCross ? 'Read More' : 'Read Less'}
              </div>
            )}
          </div>
          <div
            className='api-container-left'
            style={isLegacyClassificationTicket ? { flex: '2.2' } : undefined}
          >
            {/* <h1 style={{ fontSize: '14px' }}>Explanatory Notes</h1>
            {cur_case.data.exNotes && (
              <div
                className={`explanatory_notes-classify ${
                  !isExpanded ? 'expanded' : ''
                }`}
              >
                {formatExplanatoryNote(cur_case.data.exNotes)}
              </div>
            )}
            {cur_case.data.exNotes && (
              <div
                className='expl-notes-btn-classify'
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {!isExpanded ? 'Read More' : 'Read Less'}
              </div>
            )} */}
            <h1 style={{ fontSize: '14px', paddingTop: '0px' }}>
              Section and Chapter Notes
            </h1>
            {cur_case.data.chapterNotes && (
              <div
                className={`explanatory_notes-classify ${
                  !isExpandedSecNotes ? 'expanded' : ''
                }`}
              >
                {formatExplanatoryNote(cur_case.data.chapterNotes)}
              </div>
            )}
            {cur_case.data.chapterNotes && (
              <div
                className='expl-notes-btn-classify'
                onClick={() => setIsExpandedSecNotes(!isExpandedSecNotes)}
              >
                {!isExpandedSecNotes ? 'Read More' : 'Read Less'}
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal open={isApprovalModalOpen} onClose={handleApprovalCloseModal}>
        <ApprovalStatus
          handleClose={handleApprovalCloseModal}
          changeStatus={changeStatus}
          curStatus={cur_case.data.status}
        />
      </Modal>
    </>
  );
};

export default ClassResultsComponent;
